<!--
  One text run, wrapped in its marks from the inside out.

  The old el()-based renderer built this with a loop that wrapped the text node
  once per mark, innermost first; this recurses down the same array so the
  nesting order is identical.

  The template is written with no whitespace between tags on purpose: a newline
  inside an element becomes a text node, and `**bold**text` would then render
  with a space in it that the document does not contain.

  A comment highlight (`cover`) is the INNERMOST wrapper: a `<mark>` around the
  bare text, inside every mark of the run. Inlines.svelte has already cut the
  run where a highlight begins and ends, so a highlight never has to straddle
  two elements -- which is what would otherwise corrupt a bold or a link.
-->
<script lang="ts">
  import Self from './Marked.svelte'
  import { safeHref } from '../safe'
  import type { Cover } from '../anchors'
  import { useHighlights } from '../highlights.svelte'

  interface Mark {
    type: string
    attrs?: Record<string, any>
  }

  let {
    marks,
    index,
    text,
    cover = null,
  }: { marks: Mark[]; index: number; text: string; cover?: Cover | null } = $props()

  const hl = useHighlights()
  const active = $derived(!!cover && !!hl?.active && cover.threads.includes(hl.active))

  const spec = $derived.by(() => {
    const mark = index >= 0 ? marks[index] : null
    if (!mark) return null
    switch (mark.type) {
      case 'bold':
        return { tag: 'strong', attrs: {} as Record<string, string> }
      case 'italic':
        return { tag: 'em', attrs: {} as Record<string, string> }
      case 'code':
        return { tag: 'code', attrs: {} as Record<string, string> }
      // GFM `~~text~~`. `<s>` rather than `<del>`: `<del>` is an edit record --
      // content removed from the document, and it carries `cite`/`datetime` to
      // say by whom and when. Nothing here knows that. A struck run in one of
      // these documents means "no longer accurate / no longer relevant", which
      // is exactly what `<s>` is defined for, and it is what a screen reader
      // should announce rather than an unattributed deletion.
      case 'strike':
        return { tag: 's', attrs: {} as Record<string, string> }
      /*
       * `<u>`, which is what the Markdown serialiser writes for this mark and
       * what our parser reads back (TASK-050). It is the one mark with no
       * Markdown of its own. `<u>` is the right element rather than a styled
       * span: it means "annotated in a way that is not emphasis", which is
       * what a person reaching for underline in a document means, and it is
       * what the HTML spec reserves it for.
       */
      case 'underline':
        return { tag: 'u', attrs: {} as Record<string, string> }
      case 'link': {
        const href = safeHref(mark.attrs?.href)
        // An unsafe URL renders as plain text rather than a dead link, so
        // nothing invites a click. BUG-006.
        return href
          ? { tag: 'a', attrs: { href, rel: 'noopener noreferrer' } }
          : {
              tag: 'span',
              attrs: { class: 'unsafe-link', title: 'link removed: unsupported scheme' },
            }
      }
      default:
        return { tag: 'span', attrs: {} as Record<string, string> }
    }
  })
</script>

{#if !spec}{#if cover}<mark class="anchor" data-threads={cover.threads.join(' ')} data-state={cover.state} data-active={active ? '' : undefined}>{text}</mark>{:else}{text}{/if}{:else}<svelte:element this={spec.tag} {...spec.attrs}><Self {marks} {text} {cover} index={index - 1} /></svelte:element>{/if}
